import { useState } from 'react';
import { FieldNamesMarkedBoolean, UseFormGetValues, UseFormWatch } from 'react-hook-form';

import { useFormContext } from 'core/states';
import { IUpsertLineFormData } from 'jobs/components/JobDetails/tabs/MaterialList/PartList/UpsertLine';
import { useJobPartActions } from 'jobs/hooks';
import { IJobPart } from 'jobs/types';

interface IProps {
  jobId: number;
  jobPart?: IJobPart;
  getValues: UseFormGetValues<IUpsertLineFormData>;
  watch: UseFormWatch<IUpsertLineFormData>;
  dirtyFields: FieldNamesMarkedBoolean<IUpsertLineFormData>;
}

export const usePriceCalculation = ({ jobId, jobPart, getValues, watch, dirtyFields }: IProps) => {
  const [standardCost, setStandardCost] = useState(jobPart?.computedCost || null);
  const [purchaseCost, setPurchaseCost] = useState(jobPart?.computedPurchaseCost || null);

  const { isSubmitting } = useFormContext();

  const { calculatePrices } = useJobPartActions();

  const onCalculatePrices = async () => {
    const number = getValues('part')?.number;
    const manufacturerId = getValues('manufacturer')?.id;
    const quantity = getValues('quantity');
    const uom = getValues('uom');

    if (!number || !manufacturerId) {
      return;
    }

    const { success, record } = await calculatePrices({
      number,
      manufacturerId,
      quantity,
      uom,
      bomId: jobId,
    });

    if (!success) {
      return;
    }

    const pricesUom = record?.[watch('uom')];

    setStandardCost(pricesUom?.cost);
    setPurchaseCost(pricesUom?.purchaseCost);
  };

  const onBlurQuantity = () => !isSubmitting && dirtyFields?.quantity && onCalculatePrices();

  const onChangeUom = () => !isSubmitting && onCalculatePrices();

  const resetPrices = () => {
    setStandardCost(null);
    setPurchaseCost(null);
  };

  return {
    standardCost,
    purchaseCost,
    setStandardCost,
    setPurchaseCost,
    onBlurQuantity,
    onChangeUom,
    resetPrices,
  };
};
